import React, { useEffect, useState, useRef } from 'react'
import { useJitsiMiniPlayer } from '../../contexts/JitsiMiniPlayerContext'
import { MdDragHandle, MdCallEnd } from 'react-icons/md'
import { RiPictureInPictureExitFill } from 'react-icons/ri'
import { useIntl } from 'react-intl'
import LoaderComponent from '../LoaderComponent'
import ConfirmCloseClassroomModal from '../../pages/VirtualRoomPage/LiveClassPage/modals/ConfirmCloseClassroomModal'
import moment from 'moment'

const GlobalJitsiMiniPlayer = () => {
  const intl = useIntl()
  const {
    isVisible,
    position,
    meetingData,
    returnPageInfo,
    shouldCheckForReturn,
    updatePosition,
    stopMeeting,
    returnToOriginalPage,
    setJitsiApi,
    checkAndCloseIfOnOriginalPage,
    originalJitsiApi,
    originalContainerId,
    miniPlayerContainerId,
    forceJitsiResize,
    executeOriginalCloseRoom,
    isTutor,
    isActivatingMiniPlayer,
    classEndTime,
    executeCloseRoomInOriginalContext,
    registerAttendanceDirectly
  } = useJitsiMiniPlayer()

  // Don't render if not visible or no valid meeting data
  if (!isVisible || !meetingData || meetingData.roomName === 'dummy-room') {
    return null
  }

  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isResizing, setIsResizing] = useState(false)
  const [resizeCorner, setResizeCorner] = useState(null) // 'bottom-right' or 'bottom-left'
  const [size, setSize] = useState({ width: 400, height: 300 })
  const [localLoading, setLocalLoading] = useState(false) // Start as false since we're using existing connection
  const [confirmModal, setConfirmModal] = useState(false)
  const [shouldHideReturnButton, setShouldHideReturnButton] = useState(false)
  const previousUrlRef = useRef('')

  // Size constraints
  const MIN_WIDTH = 300
  const MIN_HEIGHT = 200
  const MAX_WIDTH = 800
  const MAX_HEIGHT = 600

  const isInContentPageWithQuestions = () => {
    const currentPath = window.location.pathname
    const contentPagePattern = /^\/(tutor-room|student-room)\/\d+\/\d+\/\d+\/\d+\/?$/
    return contentPagePattern.test(currentPath)
  }

  const shouldShowReturnButton = () => {
    if (!returnPageInfo) {
      return false
    }

    if (isInContentPageWithQuestions()) {
      return false
    }

    return true
  }

  // Use the original Jitsi API instead of creating a new one
  useEffect(() => {
    if (originalJitsiApi && meetingData && meetingData.roomName !== 'dummy-room') {
      setJitsiApi(originalJitsiApi)
      setLocalLoading(false) // Connection is already established

      // Check if the interface is in the mini player container, if not, move it
      const miniPlayerContainer = document.getElementById('global-jitsi-container')
      const originalContainer = document.getElementById(originalContainerId)

      if (miniPlayerContainer && originalContainer) {
        // Check if the interface is in the original container (needs to be moved)
        if (originalContainer.children.length > 0 && miniPlayerContainer.children.length === 0) {
          setTimeout(() => {
            forceJitsiResize(originalJitsiApi, miniPlayerContainer)
          }, 100)
        }
      }
    }
  }, [originalJitsiApi, meetingData, setJitsiApi, originalContainerId, forceJitsiResize])

  // Force Jitsi resize when mini player size changes
  useEffect(() => {
    if (originalJitsiApi && isVisible && miniPlayerContainerId) {
      const container = document.getElementById(miniPlayerContainerId)
      if (container) {
        // Force resize after a short delay to ensure the container is ready
        setTimeout(() => {
          forceJitsiResize(originalJitsiApi, container)
        }, 150)
      }
    }
  }, [size, originalJitsiApi, isVisible, miniPlayerContainerId, forceJitsiResize])

  // Force initial resize when mini player becomes visible
  useEffect(() => {
    if (isVisible && originalJitsiApi && miniPlayerContainerId) {
      const container = document.getElementById(miniPlayerContainerId)
      if (container) {
        // Force initial resize with multiple attempts to ensure it works
        const resizeAttempts = [100, 300, 500, 1000]
        resizeAttempts.forEach(delay => {
          setTimeout(() => {
            forceJitsiResize(originalJitsiApi, container)
          }, delay)
        })
      }
    }
  }, [isVisible, originalJitsiApi, miniPlayerContainerId, forceJitsiResize])

  // Check and move interface when mini player becomes visible
  useEffect(() => {
    if (isVisible && originalJitsiApi && originalContainerId && miniPlayerContainerId) {
      const miniPlayerContainer = document.getElementById(miniPlayerContainerId)
      const originalContainer = document.getElementById(originalContainerId)

      if (miniPlayerContainer && originalContainer) {
        // If interface is in original container, move it to mini player
        if (originalContainer.children.length > 0 && miniPlayerContainer.children.length === 0) {
          setTimeout(() => {
            // Move all children from original container to mini player container
            while (originalContainer.firstChild) {
              miniPlayerContainer.appendChild(originalContainer.firstChild)
            }

            // Force resize after moving
            forceJitsiResize(originalJitsiApi, miniPlayerContainer)
          }, 150)
        } else if (miniPlayerContainer.children.length === 0) {
          // If mini player container is empty, try to find Jitsi interface elsewhere
          const jitsiIframe = document.querySelector('iframe[src*="meet.aulapp.com.br"]')
          if (jitsiIframe && jitsiIframe.parentElement) {
            setTimeout(() => {
              miniPlayerContainer.appendChild(jitsiIframe)
              forceJitsiResize(originalJitsiApi, miniPlayerContainer)
            }, 150)
          }
        }
      }
    }
  }, [isVisible, originalJitsiApi, originalContainerId, miniPlayerContainerId, forceJitsiResize])

  useEffect(() => {
    const handleMouseMove = e => {
      if (isDragging) {
        e.preventDefault()
        const playerWidth = size.width
        const playerHeight = size.height + 30 // Include drag handle height

        let newX = e.clientX - dragOffset.x
        let newY = e.clientY - dragOffset.y

        newX = Math.max(0, Math.min(newX, window.innerWidth - playerWidth))
        newY = Math.max(0, Math.min(newY, window.innerHeight - playerHeight))

        updatePosition({ x: newX, y: newY })
      } else if (isResizing) {
        e.preventDefault()
        const currentPosition = position || getInitialPosition()

        if (resizeCorner === 'bottom-right') {
          const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX - currentPosition.x))
          const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, e.clientY - currentPosition.y))
          setSize({ width: newWidth, height: newHeight })
        } else if (resizeCorner === 'bottom-left') {
          const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, currentPosition.x + size.width - e.clientX))
          const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, e.clientY - currentPosition.y))

          // Calculate new position to keep the right edge fixed
          const newX = currentPosition.x + (size.width - newWidth)

          setSize({ width: newWidth, height: newHeight })
          updatePosition({ x: newX, y: currentPosition.y })
        }
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setIsResizing(false)

      // Force Jitsi resize when user finishes resizing
      if (originalJitsiApi && miniPlayerContainerId) {
        const container = document.getElementById(miniPlayerContainerId)
        if (container) {
          setTimeout(() => {
            forceJitsiResize(originalJitsiApi, container)
          }, 100)
        }
      }
    }

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, isResizing, dragOffset, updatePosition, position, size, resizeCorner])

  const handleMouseDownOnDragHandle = e => {
    e.preventDefault()
    setIsDragging(true)
    const currentPosition = position || getInitialPosition()
    setDragOffset({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y
    })
  }

  const handleMouseDownOnResizeHandle = (corner) => e => {
    e.preventDefault()
    setIsResizing(true)
    setResizeCorner(corner)
  }

  const handleCloseRoom = () => {
    // Don't execute close room function if we're currently activating the mini player
    if (isActivatingMiniPlayer) {
      return
    }

    // For tutors, show confirmation modal before closing
    if (isTutor) {
      setConfirmModal(true)
    } else {
      // Prefer direct registration to avoid context issues
      if (registerAttendanceDirectly) {
        registerAttendanceDirectly()
      } else {
        // Fallback to original function/context
        try {
          executeOriginalCloseRoom()
        } catch (error) {
          try {
            executeCloseRoomInOriginalContext()
          } catch (fallbackError) {}
        }
      }
    }
  }

  const cancelModal = () => {
    setConfirmModal(false)
  }

  // Force resize when dragging ends
  useEffect(() => {
    if (!isDragging && !isResizing && originalJitsiApi && miniPlayerContainerId) {
      const container = document.getElementById(miniPlayerContainerId)
      if (container) {
        setTimeout(() => {
          forceJitsiResize(originalJitsiApi, container)
        }, 100)
      }
    }
  }, [isDragging, isResizing, originalJitsiApi, miniPlayerContainerId, forceJitsiResize])

  // Check if we're on the original page and close miniplayer if we are
  useEffect(() => {
    if (isVisible) {
      checkAndCloseIfOnOriginalPage()
    }
  }, [isVisible, checkAndCloseIfOnOriginalPage])

  // Check periodically if class has ended to hide return button (do NOT auto-register leaving_time)
  useEffect(() => {
    if (!isVisible || !classEndTime) return

    const checkClassEnded = () => {
      const now = moment()
      const endTime = moment(classEndTime)
      const hasEnded = now.isAfter(endTime)
      setShouldHideReturnButton(hasEnded)
    }

    // Check immediately
    checkClassEnded()

    const interval = setInterval(checkClassEnded, 6000)

    return () => {
      clearInterval(interval)
    }
  }, [isVisible, classEndTime])

  // Listen for URL changes to automatically close miniplayer when returning to original page
  useEffect(() => {
    if (!isVisible || !shouldCheckForReturn) return

    const handleUrlChange = () => {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash

      // Only check if URL actually changed
      if (currentUrl !== previousUrlRef.current) {
        previousUrlRef.current = currentUrl
        checkAndCloseIfOnOriginalPage()
      }
    }

    // Initialize with current URL
    previousUrlRef.current = window.location.pathname + window.location.search + window.location.hash

    // Check immediately
    handleUrlChange()

    // Listen for browser navigation (back/forward buttons)
    window.addEventListener('popstate', handleUrlChange)

    // Check periodically for programmatic navigation (React Router)
    const intervalId = setInterval(handleUrlChange, 500)

    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      clearInterval(intervalId)
    }
  }, [isVisible, shouldCheckForReturn, checkAndCloseIfOnOriginalPage])

  // Esconde o botão de retorno assim que a chamada realmente encerrar (readyToClose)
  useEffect(() => {
    if (!isVisible || !originalJitsiApi) return

    let handler = null

    try {
      if (typeof originalJitsiApi.on === 'function') {
        handler = () => {
          setShouldHideReturnButton(true)
        }
        originalJitsiApi.on('readyToClose', handler)
      } else if (typeof originalJitsiApi.addEventListener === 'function') {
        handler = () => {
          setShouldHideReturnButton(true)
        }
        originalJitsiApi.addEventListener('readyToClose', handler)
      }
    } catch (e) {}

    return () => {
      try {
        if (handler) {
          if (typeof originalJitsiApi.off === 'function') {
            originalJitsiApi.off('readyToClose', handler)
          } else if (typeof originalJitsiApi.removeEventListener === 'function') {
            originalJitsiApi.removeEventListener('readyToClose', handler)
          }
        }
      } catch (e) {}
    }
  }, [isVisible, originalJitsiApi])

  // Calculate initial position for bottom-right corner
  const getInitialPosition = () => {
    if (position) return position

    // Position in bottom-right corner with some margin
    const margin = 20
    const dragHandleHeight = 30
    const totalHeight = size.height + dragHandleHeight

    let x = window.innerWidth - size.width - margin
    let y = window.innerHeight - totalHeight - margin

    // Ensure it doesn't go off-screen
    x = Math.max(0, Math.min(x, window.innerWidth - size.width))
    y = Math.max(0, Math.min(y, window.innerHeight - totalHeight))

    return { x, y }
  }

  const currentPosition = getInitialPosition()

  const containerStyle = {
    position: 'fixed',
    top: `${currentPosition.y}px`,
    left: `${currentPosition.x}px`,
    width: `${size.width}px`,
    height: `${size.height}px`, // Remove the extra 30px
    zIndex: 99999999,
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    borderRadius: '8px',
    overflow: 'visible',
    transition: (isDragging || isResizing) ? 'none' : 'width 0.3s ease, height 0.3s ease',
    backgroundColor: '#fff',
    cursor: isDragging ? 'grabbing' : 'default'
  }

  const iframeStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0',
    left: '0',
    pointerEvents: (isDragging || isResizing) ? 'none' : 'auto'
  }

  // Add CSS to force Jitsi to adapt to mini player size
  useEffect(() => {
    if (isVisible && miniPlayerContainerId) {
      const container = document.getElementById(miniPlayerContainerId)
      if (container) {
        // Add CSS to force Jitsi elements to fit the mini player
        const style = document.createElement('style')
        style.id = 'jitsi-mini-player-styles'
        style.textContent = `
          #${miniPlayerContainerId} iframe {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
          }
          #${miniPlayerContainerId} [data-testid="meeting"] {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
          }
          #${miniPlayerContainerId} [class*="jitsi"] {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            overflow: hidden !important;
          }
          #${miniPlayerContainerId} * {
            box-sizing: border-box !important;
          }
        `
        document.head.appendChild(style)

        return () => {
          // Remove the style when component unmounts
          const existingStyle = document.getElementById('jitsi-mini-player-styles')
          if (existingStyle) {
            existingStyle.remove()
          }
        }
      }
    }
  }, [isVisible, miniPlayerContainerId])

  const buttonStyle = {
    position: 'absolute',
    top: '15px',
    zIndex: 3,
    background: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const returnButtonStyle = {
    ...buttonStyle,
    left: '15px',
    background: 'rgba(0, 0, 0, 0.9)',
    padding: '8px 12px'
  }

  const endCallButtonStyle = {
    ...buttonStyle,
    right: '15px',
    background: 'rgba(220, 53, 69, 0.8)',
    padding: '8px 12px'
  }

  const dragHandleStyle = {
    position: 'absolute',
    top: '-30px',
    left: '0',
    width: '100%',
    height: '30px',
    background: 'rgba(0, 0, 0, 0.8)',
    cursor: 'grab',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    userSelect: 'none',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px'
  }

  const resizeHandleStyle = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '20px',
    height: '20px',
    cursor: 'nw-resize',
    zIndex: 2,
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '0 0 8px 0'
  }

  const resizeHandleLeftStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '20px',
    height: '20px',
    cursor: 'ne-resize',
    zIndex: 2,
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '0 0 0 8px'
  }

  return (
    <>
      <div style={containerStyle}>
        <div
          style={dragHandleStyle}
          onMouseDown={handleMouseDownOnDragHandle}
        >
          <MdDragHandle size={24} />
          <div style={{
            position: 'absolute',
            top: '5px',
            right: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '12px'
          }}>
            {/* Removed connection status display */}
          </div>
        </div>

        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          borderRadius: '0 0 8px 8px', // Only round the bottom corners
          overflow: 'hidden'
        }}>
          {localLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '10px',
              borderRadius: '4px'
            }}>
              <LoaderComponent />
            </div>
          )}

          <div id="global-jitsi-container" style={{
            ...iframeStyle,
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }} />

          {shouldShowReturnButton() && returnPageInfo && !shouldHideReturnButton && (
            <button
              onClick={returnToOriginalPage}
              title={intl.formatMessage({ id: 'jitsi.returnToClass', defaultMessage: 'Voltar para a aula' })}
              style={returnButtonStyle}
            >
              <RiPictureInPictureExitFill size={20} />
            </button>
          )}

          <button
            onClick={handleCloseRoom}
            title={intl.formatMessage({ id: 'jitsi.endCall', defaultMessage: 'Encerrar chamada' })}
            style={endCallButtonStyle}
          >
            <MdCallEnd size={20} />
          </button>

          <div
            style={resizeHandleStyle}
            onMouseDown={handleMouseDownOnResizeHandle('bottom-right')}
          />
          <div
            style={resizeHandleLeftStyle}
            onMouseDown={handleMouseDownOnResizeHandle('bottom-left')}
          />
        </div>
      </div>

      {confirmModal && (
        <ConfirmCloseClassroomModal
          setConfirmModal={setConfirmModal}
          confirmModal={confirmModal}
          cancelModal={cancelModal}
          handleClose={() => {
            try {
              // Garante registrar presença do tutor antes de fechar
              if (registerAttendanceDirectly) {
                registerAttendanceDirectly(true)
              }
              executeOriginalCloseRoom()
            } catch (error) {
              try {
                executeCloseRoomInOriginalContext()
              } catch (fallbackError) {
                registerAttendanceDirectly()
              }
            }
          }}
          handleExit={() => {
            try {
              if (registerAttendanceDirectly) {
                registerAttendanceDirectly(true)
              }
              executeOriginalCloseRoom()
            } catch (error) {
              try {
                executeCloseRoomInOriginalContext()
              } catch (fallbackError) {
                registerAttendanceDirectly()
              }
            }
          }}
        />
      )}
    </>
  )
}

export default GlobalJitsiMiniPlayer
